/**
 * Pesquisa de cadastro por digitação + lupa (v6): um componente para cliente, fornecedor,
 * produto e vendedor. Padrão combobox (setas, Enter, Esc), F2 ou Alt+↓ abre a lupa.
 *
 * - Busca 250 ms depois de parar de digitar; cada texto tem a própria consulta e a anterior é
 *   cancelada, então a lista nunca mostra o resultado de um texto antigo.
 * - Enter com a lista aberta só seleciona (não chega ao formulário em volta).
 * - Os dados vêm da API com a empresa ativa (RLS); nada é guardado entre empresas.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type Ref } from "react";
import { LookupDialog } from "./LookupDialog";
import { lookupLabel, type LookupSource } from "./types";

const DEBOUNCE_MS = 250;
const SUGGESTIONS = 10;

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export type EntityLookupProps<T> = {
  source: LookupSource<T>;
  /** id escolhido ("" = nenhum) */
  value: string;
  /** texto mostrado para o escolhido */
  label: string;
  onChange: (id: string, label: string, item?: T) => void;
  disabled?: boolean;
  /** permite escolher inativos (padrão: não) */
  allowInactive?: boolean;
  id?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  /** depois de escolher (para levar o foco ao próximo campo) */
  onSelected?: (item: T) => void;
};

export function EntityLookup<T>({
  source,
  value,
  label,
  onChange,
  disabled,
  allowInactive = false,
  id,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
  inputRef,
  onSelected,
}: EntityLookupProps<T>) {
  const autoId = useId();
  const inputId = id ?? `lookup-${autoId}`;
  const listId = `${inputId}-lista`;
  const [text, setText] = useState(label);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [dialog, setDialog] = useState(false);
  const localRef = useRef<HTMLInputElement | null>(null);

  // o rótulo muda por fora (pedido recarregado, formulário limpo): o texto acompanha
  useEffect(() => setText(label), [label]);

  const q = useDebounced(text, DEBOUNCE_MS).trim();
  const searching = open && !value && q.length >= 1 && q === text.trim();
  const found = useQuery({
    queryKey: [...source.key, q],
    queryFn: ({ signal }) =>
      source.search(
        { q, limit: SUGGESTIONS, offset: 0, includeInactive: false, filters: {} },
        signal,
      ),
    enabled: searching,
    staleTime: 15_000,
  });
  const items = searching ? (found.data?.items ?? []) : [];
  const rows = items.map((it) => ({ it, row: source.toRow(it) }));
  const listOpen = searching && (rows.length > 0 || found.data !== undefined);

  function choose(item: T) {
    const row = source.toRow(item);
    const l = lookupLabel(row);
    setText(l);
    setOpen(false);
    onChange(row.id, l, item);
    onSelected?.(item);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "F2" || (e.altKey && e.key === "ArrowDown")) {
      e.preventDefault();
      setDialog(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, Math.max(rows.length - 1, 0)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
      return;
    }
    if (e.key === "Enter" && listOpen) {
      // a lista aberta é dona do Enter: seleciona e não deixa o Enter seguir adiante
      e.preventDefault();
      e.stopPropagation();
      const pick = rows[active];
      if (pick && !pick.row.inactive) choose(pick.it);
      return;
    }
    if (e.key === "Escape" && listOpen) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  }

  const setRefs = (el: HTMLInputElement | null) => {
    localRef.current = el;
    if (typeof inputRef === "function") inputRef(el);
    else if (inputRef) inputRef.current = el;
  };

  return (
    <div className="relative flex gap-1">
      <input
        ref={setRefs}
        id={inputId}
        role="combobox"
        aria-expanded={listOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        {...(listOpen && rows[active] ? { "aria-activedescendant": `${listId}-${active}` } : {})}
        {...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {})}
        {...(ariaInvalid ? { "aria-invalid": true } : {})}
        autoComplete="off"
        className="h-11 w-full min-w-0 rounded-lg border border-input bg-card px-3.5 text-[15px] text-ellipsis outline-none transition-shadow placeholder:text-muted-foreground/80 focus:border-caramel focus:ring-4 focus:ring-caramel/20 disabled:cursor-not-allowed disabled:bg-muted aria-[invalid=true]:border-destructive"
        value={text}
        // nome longo: o texto completo aparece ao passar o mouse
        {...(value && text ? { title: text } : {})}
        disabled={disabled}
        placeholder={source.placeholder}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
          if (value) onChange("", "");
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-input bg-card text-muted-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
        aria-label={`Pesquisar ${source.noun} (F2)`}
        title={`Pesquisar ${source.noun} (F2)`}
        disabled={disabled}
        onClick={() => setDialog(true)}
      >
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" />
          <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
      <ul
        id={listId}
        role="listbox"
        aria-label={`Sugestões de ${source.noun}`}
        hidden={!listOpen}
        className="absolute top-11 z-20 max-h-72 w-full overflow-auto rounded-md border bg-card text-sm shadow"
      >
        {rows.map(({ it, row }, n) => (
          <li
            key={row.id}
            id={`${listId}-${n}`}
            role="option"
            aria-selected={n === active}
            aria-disabled={row.inactive || undefined}
            className={`cursor-pointer px-3 py-1.5 ${n === active ? "bg-muted" : ""} ${
              row.inactive ? "opacity-60" : ""
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setActive(n)}
            onClick={() => !row.inactive && choose(it)}
          >
            <span className="font-mono text-xs">{row.code}</span> — {row.name}
            {row.secondary && (
              <span className="block text-xs text-muted-foreground">{row.secondary}</span>
            )}
          </li>
        ))}
        {found.data && rows.length === 0 && (
          <li
            role="option"
            aria-selected={false}
            aria-disabled
            className="px-3 py-1.5 text-muted-foreground"
          >
            {source.empty}
          </li>
        )}
      </ul>
      {dialog && (
        <LookupDialog
          source={source}
          initialText={value ? "" : text}
          allowInactive={allowInactive}
          onClose={() => {
            setDialog(false);
            localRef.current?.focus();
          }}
          onPick={(item) => {
            setDialog(false);
            choose(item);
            localRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}
